import { Add as AddIcon, FolderOpenOutlined, GroupOutlined, SearchOffOutlined, SearchOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, InputAdornment, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { parseApiError } from '../../../library/apiErrorHelper';
import { newEvaluationLink, patientDetailLinkFor } from '../../../library/constants';
import { STATUS_LABELS } from '../../../library/membershipDisplay';
import { AddPatientFormValues, IPatient, MembershipStatus, toInviteCustomerRequest, toResendInvitationRequest } from '../../../library/models/clinic';
import { useDebouncedValue } from '../../../library/useDebouncedValue';
import { useIsCompact } from '../../../library/useIsCompact';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactInfiniteQuery, useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useAuth } from '../../../contexts/AuthContext';
import { AddPatientDialog } from '../../addPatientDialog/AddPatientDialog';
import { ConfirmationDialog } from '../../confirmationDialog/confirmationDialog';
import { useSnackbar } from '../../layout/snackbarProvider';
import { MembershipStatusChip } from '../../membershipStatusChip/membershipStatusChip';
import { PatientTherapists } from '../../patientTherapists/patientTherapists';
import { RecordCard } from '../../recordCard/RecordCard';
import { cardListStyles } from '../../recordCard/recordCard.styles';
import { alertStyles, emptyStateBoxStyles, emptyStateIconStyles, filterBarStyles, headerActionsStyles, headerBoxStyles, headerCellStyles, identifierCellStyles, loadingBoxStyles, phoneCellStyles, phoneMissingStyles, resultCountStyles, rowActionsBoxStyles, searchFieldStyles, showMoreBoxStyles, statusFieldStyles, subtitleStyles, tableContainerStyles, therapistsCellStyles, titleStyles } from './patients.styles';
import { PersonLabel } from '../../userAvatar/UserAvatar';

/** The filter's own value, not a membership state -- hence its own constant. */
const ALL_STATUSES = 'ALL';

type StatusFilter = MembershipStatus | typeof ALL_STATUSES;

/**
 * No INVITATION_EXPIRED: a patient's invitation does not expire, so nobody on this page
 * is ever in that state and the option could only ever answer with an empty list. The
 * status itself is still real -- staff invitations last a day -- which is why it stays in
 * `MembershipStatus` and in the labels.
 */
const STATUS_OPTIONS: MembershipStatus[] = ['ACTIVE', 'PENDING', 'REVOKED'];

/**
 * How many arrive at once, and how many each "show more" adds.
 *
 * Twenty rather than five: unlike a patient's evaluations, this is a list somebody scans
 * looking for a name, and the first screenful should be worth scanning. It is also the
 * API's own default, so the two agree about what a page is.
 */
const PATIENTS_PAGE_SIZE = 20;

/**
 * A therapist's working view: everyone their clinic treats, searchable by the patient's own
 * name, email or phone number, and each row saying who the patient belongs to.
 *
 * The search deliberately does not cover the therapists a patient belongs to, though it once
 * did. Those are two questions with different answers: a therapist looking for Ana's patients
 * would also be handed every patient called Ana, with nothing to tell the two apart. "My
 * patients only" asks the first one exactly, which is why it is a toggle and not a term.
 *
 * Belonging is a union, not an assignment: the therapist who evaluated somebody keeps
 * them even if they never buy anything, and a colleague who provides them a service is
 * added rather than substituted. Showing the names is what makes that legible -- the
 * "My Patients" toggle can only ever say whether a patient is yours, never that they are
 * also somebody else's.
 *
 * Clinic-wide by default, one page at a time, and every way of narrowing it is a request.
 * The search and the status dropdown used to filter the array this page had already been
 * sent, which is why nothing appeared in the network tab when you typed: it could only ever
 * find what happened to have been downloaded, the "N of M" counted the browser's memory
 * rather than the clinic, and `String.includes` is accent-sensitive, so "Farcas" never
 * found "Fărcaș". All three narrow in SQL now, beside the paging, where the rules already
 * live and the collation ignores diacritics.
 *
 * Nothing here is editable. Names belong to the account rather than to the clinic, so
 * the one action a therapist has over a patient is to send their invitation again.
 */
export const Patients = (): JSX.Element => {
    const theme = useTheme();
    const navigate: NavigateFunction = useNavigate();
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();
    // Only to tell the caller's own chip from a colleague's -- this page shows the same
    // patients to everyone who can reach it.
    const { user } = useAuth();
    const isCompact = useIsCompact();

    const [query, setQuery] = useState('');
    const [status, setStatus] = useState<StatusFilter>(ALL_STATUSES);
    const [mineOnly, setMineOnly] = useState(false);
    /** Who the confirmation is about, or null when it is closed. */
    const [resending, setResending] = useState<IPatient | null>(null);
    const [isAddOpen, setIsAddOpen] = useState(false);

    // The field keeps its own value so typing stays instant; the query reads the settled
    // one, so a name is one request rather than one per letter.
    const debouncedQuery = useDebouncedValue(query);

    const { data, isLoading, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
        useReactInfiniteQuery<IPatient>(
            services.users.getPatients(PATIENTS_PAGE_SIZE, {
                mine: mineOnly,
                search: debouncedQuery,
                status: status === ALL_STATUSES ? undefined : status,
            })
        );
    const inviteCustomer = useReactMutation(services.users.inviteCustomerMutation());
    // Its own instance rather than the one above: that one drives the resend confirmation
    // with callbacks, and sharing it would make one dialog's pending state the other's.
    const addPatient = useReactMutation(services.users.inviteCustomerMutation());

    /**
     * Whether the list was actually fetched, which is not the same as "isError is false".
     * A query can settle without data and without an error -- React Query pauses one it
     * believes cannot reach the network -- and reading that as an empty result would tell
     * a therapist their clinic has no patients when nothing was ever loaded.
     */
    const hasLoaded = data !== undefined;
    const patients = useMemo(() => data?.pages.flatMap(page => page.items) ?? [], [data]);
    /** Every page reports the same total, so the first one answers for all of them. */
    const totalItems = data?.pages[0]?.totalItems ?? 0;
    const remaining = Math.max(totalItems - patients.length, 0);

    // Four different nothings, and they call for different words: nothing loaded, nobody
    // on the books, nobody linked to the caller, and nobody matching what was typed.
    //
    // Which one it is can no longer be read off the length of the list, because the server
    // has already applied the filters -- an empty answer to a search and an empty clinic
    // look identical on the wire. So the filters themselves decide, and the *settled* term
    // does it rather than the typed one, or the empty state would flicker to "no matches"
    // before the request that decides it has even been made.
    const isFiltered = debouncedQuery.trim().length > 0 || status !== ALL_STATUSES;
    const isEmpty = hasLoaded && totalItems === 0;
    const showNoMatches = isEmpty && isFiltered;
    const showEmptyClinic = isEmpty && !isFiltered && !mineOnly;
    const showNoMinePatients = isEmpty && !isFiltered && mineOnly;

    /**
     * Adds a patient to the clinic without an evaluation.
     *
     * Rejects on failure rather than handling it: the dialog owns the mapping from an API
     * error back onto its own fields, and only a success closes it. Refetching is what
     * shows the new row -- it arrives from the server with its real status and no
     * therapists, which nothing here could invent.
     */
    const submitAddPatient = async (values: AddPatientFormValues): Promise<void> => {
        await addPatient.mutateAsync(toInviteCustomerRequest(values));

        showSnackbar(
            translate(ResourcesIds.ADD_PATIENT_SUCCESS, {
                emailAddress: values.emailAddress.trim(),
            }),
            'success'
        );
        setIsAddOpen(false);
        refetch();
    };

    /**
     * Sends the invitation again: the same email, with the same link. The list is
     * refetched afterwards so the row shows what the server now says, though for a pending
     * patient nothing on it changes -- the snackbar is the proof the mail went.
     */
    const confirmResend = (): void => {
        if (!resending) {
            return;
        }

        const member = resending;
        setResending(null);

        inviteCustomer.mutate(toResendInvitationRequest(member), {
            onSuccess: () => {
                showSnackbar(
                    translate(ResourcesIds.PATIENTS_RESEND_INVITATION_SUCCESS, {
                        emailAddress: member.emailAddress,
                    }),
                    'success'
                );
                refetch();
            },
            onError: (error: unknown) => {
                const parsed = parseApiError(error);
                showSnackbar(
                    parsed.message ?? translate(ResourcesIds.PATIENTS_RESEND_INVITATION_ERROR),
                    'error'
                );
            },
        });
    };

    return (
        <Box>
            <Box sx={headerBoxStyles}>
                <Box>
                    <Typography variant="h4" component="h1" sx={titleStyles}>
                        {translate(ResourcesIds.PATIENTS_TITLE)}
                    </Typography>
                    <Typography variant="body1" sx={subtitleStyles}>
                        {translate(ResourcesIds.PATIENTS_SUBTITLE)}
                    </Typography>
                </Box>

                <Box sx={headerActionsStyles}>
                    {/* The lighter of the two: it creates an account and nothing else.
                        Recording an evaluation also creates one, and a clinical record
                        with it, which is why that stays the filled button. */}
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setIsAddOpen(true)}
                    >
                        {translate(ResourcesIds.ADD_PATIENT_ACTION)}
                    </Button>

                    <Button variant="contained" onClick={() => navigate(newEvaluationLink)}>
                        {translate(ResourcesIds.EVALUATION_NEW)}
                    </Button>
                </Box>
            </Box>

            {!isLoading && !hasLoaded && (
                <Alert severity="error" sx={alertStyles}>
                    {translate(ResourcesIds.PATIENTS_LOAD_ERROR)}
                </Alert>
            )}

            {/* Every control stays visible whenever one of them is doing something, and
                that now includes the search box: with the filtering on the server, hiding
                it on an empty result would take away the field the therapist just typed
                into, mid-word. The "mine" toggle survives an empty list for the same
                reason it always did -- it is the way back to the full one. */}
            {hasLoaded && (totalItems > 0 || isFiltered || mineOnly) && (
                <Box sx={filterBarStyles}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={mineOnly}
                                onChange={event => setMineOnly(event.target.checked)}
                                size="small"
                            />
                        }
                        label={translate(ResourcesIds.PATIENTS_MINE_FILTER_LABEL)}
                    />

                    <TextField
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        label={translate(ResourcesIds.PATIENTS_SEARCH_LABEL)}
                        size="small"
                        sx={searchFieldStyles}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchOutlined fontSize="small" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />

                    <TextField
                        select
                        value={status}
                        onChange={event => setStatus(event.target.value as StatusFilter)}
                        label={translate(ResourcesIds.PATIENTS_STATUS_FILTER_LABEL)}
                        size="small"
                        sx={statusFieldStyles}
                    >
                        <MenuItem value={ALL_STATUSES}>
                            {translate(ResourcesIds.PATIENTS_STATUS_FILTER_ALL)}
                        </MenuItem>
                        {STATUS_OPTIONS.map(option => (
                            <MenuItem key={option} value={option}>
                                {translate(STATUS_LABELS[option])}
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Loaded of matching, not loaded of downloaded: the total comes from
                        the server, so it describes the clinic rather than this page. */}
                    {(isFiltered || hasNextPage) && (
                        <Typography variant="body2" sx={resultCountStyles}>
                            {translate(ResourcesIds.PATIENTS_COUNT, {
                                shown: patients.length,
                                total: totalItems,
                            })}
                        </Typography>
                    )}
                </Box>
            )}

            {isLoading ? (
                <Box sx={loadingBoxStyles}>
                    <CircularProgress size={60} />
                </Box>
            ) : !hasLoaded ? null : showEmptyClinic ? (
                <Box sx={emptyStateBoxStyles}>
                    <GroupOutlined sx={emptyStateIconStyles(theme)} />
                    <Typography variant="h6" gutterBottom>
                        {translate(ResourcesIds.PATIENTS_EMPTY_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={subtitleStyles}>
                        {translate(ResourcesIds.PATIENTS_EMPTY_SUBTITLE)}
                    </Typography>
                </Box>
            ) : showNoMinePatients ? (
                <Box sx={emptyStateBoxStyles}>
                    <GroupOutlined sx={emptyStateIconStyles(theme)} />
                    <Typography variant="h6" gutterBottom>
                        {translate(ResourcesIds.PATIENTS_EMPTY_MINE_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={subtitleStyles}>
                        {translate(ResourcesIds.PATIENTS_EMPTY_MINE_SUBTITLE)}
                    </Typography>
                </Box>
            ) : showNoMatches ? (
                <Box sx={emptyStateBoxStyles}>
                    <SearchOffOutlined sx={emptyStateIconStyles(theme)} />
                    <Typography variant="h6" gutterBottom>
                        {translate(ResourcesIds.PATIENTS_NO_MATCHES_TITLE)}
                    </Typography>
                    <Typography variant="body2" sx={subtitleStyles}>
                        {translate(ResourcesIds.PATIENTS_NO_MATCHES_SUBTITLE)}
                    </Typography>
                </Box>
            ) : isCompact ? (
                <Box sx={cardListStyles}>
                    {patients.map(patient => (
                        <RecordCard
                            key={patient.id}
                            title={<PersonLabel person={patient} />}
                            titleAdornment={
                                <MembershipStatusChip
                                    status={patient.status}
                                    onResend={() => setResending(patient)}
                                />
                            }
                            fields={[
                                {
                                    label: translate(ResourcesIds.EMAIL_LABEL),
                                    value: patient.emailAddress,
                                },
                                {
                                    label: translate(ResourcesIds.PHONE_LABEL),
                                    value: patient.phoneNumber ?? translate(ResourcesIds.PHONE_NOT_GIVEN),
                                },
                                {
                                    label: translate(ResourcesIds.PATIENTS_THERAPISTS_LABEL),
                                    value: (
                                        <PatientTherapists
                                            therapists={patient.therapists}
                                            currentUserId={user?.id}
                                        />
                                    ),
                                },
                            ]}
                            actions={
                                // No Tooltip on the card: the button already says "Open",
                                // and a tooltip on a touch screen is a long-press nobody
                                // performs. The table keeps its own for the same reason
                                // in reverse -- a pointer is what tooltips are for.
                                <Button
                                    size="small"
                                    startIcon={<FolderOpenOutlined />}
                                    onClick={() => navigate(patientDetailLinkFor(patient.id))}
                                >
                                    {translate(ResourcesIds.PATIENTS_OPEN)}
                                </Button>
                            }
                        />
                    ))}
                </Box>
            ) : (
                <TableContainer component={Paper} sx={tableContainerStyles}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.MEMBER_NAME_LABEL)}
                                </TableCell>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.EMAIL_LABEL)}
                                </TableCell>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.PHONE_LABEL)}
                                </TableCell>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.PATIENTS_THERAPISTS_LABEL)}
                                </TableCell>
                                <TableCell sx={headerCellStyles}>
                                    {translate(ResourcesIds.MEMBER_STATUS_LABEL)}
                                </TableCell>
                                <TableCell sx={headerCellStyles} />
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {patients.map(patient => (
                                <TableRow key={patient.id}>
                                    <TableCell><PersonLabel person={patient} /></TableCell>
                                    <TableCell sx={identifierCellStyles}>{patient.emailAddress}</TableCell>
                                    <TableCell sx={patient.phoneNumber ? phoneCellStyles : phoneMissingStyles}>
                                        {patient.phoneNumber ?? translate(ResourcesIds.PHONE_NOT_GIVEN)}
                                    </TableCell>
                                    <TableCell sx={therapistsCellStyles}>
                                        <PatientTherapists
                                            therapists={patient.therapists}
                                            currentUserId={user?.id}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <MembershipStatusChip
                                            status={patient.status}
                                            onResend={() => setResending(patient)}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={rowActionsBoxStyles}>
                                            {/* Into the patient's own page rather than straight to their
                                                evaluations: what they bought and what is left of it lives
                                                there too, and the evaluations are one click further in. */}
                                            <Tooltip title={translate(ResourcesIds.PATIENTS_OPEN)}>
                                                <Button
                                                    size="small"
                                                    startIcon={<FolderOpenOutlined />}
                                                    onClick={() => navigate(patientDetailLinkFor(patient.id))}
                                                >
                                                    {translate(ResourcesIds.PATIENTS_OPEN)}
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Drives both shapes, so a phone and a laptop are never showing different
                amounts of the same list. */}
            {hasNextPage && (
                <Box sx={showMoreBoxStyles}>
                    <Button
                        disabled={isFetchingNextPage}
                        onClick={() => fetchNextPage()}
                    >
                        {translate(ResourcesIds.PATIENTS_SHOW_MORE, { remaining })}
                    </Button>
                </Box>
            )}

            <AddPatientDialog
                isOpen={isAddOpen}
                onClose={() => setIsAddOpen(false)}
                onSubmit={submitAddPatient}
            />

            <ConfirmationDialog
                isOpen={resending !== null}
                onClose={() => setResending(null)}
                onConfirm={confirmResend}
                title={translate(ResourcesIds.PATIENTS_RESEND_INVITATION_TITLE)}
                description={translate(ResourcesIds.PATIENTS_RESEND_INVITATION_DESCRIPTION)}
                confirmMessage={translate(ResourcesIds.PATIENTS_RESEND_INVITATION_CONFIRM)}
                denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
                confirmColor="primary"
            />
        </Box>
    );
};
